import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { types } from 'mobx-state-tree';
import { api } from 'src/api';
import { storeBillFile } from 'src/stores/billAutomation/uploadBillFile';
import { BILL_READY_BATCH } from 'src/api/billReady';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { isHttpClientCancel } from 'src/core/services/api/httpClient';
import { createAsync, createSilentAsync } from 'src/utils/asyncModel';

/** The service caps the page size at 100, whatever is asked for. */
const DEFAULT_PAGE_SIZE = 100;

/**
 * What one view of the list asks for: page, size, sort and every filter, already translated into
 * what the service takes. Built by `toBillsRequestParams` and passed through untouched, so the
 * store never has to know that a status is a label or a property an address.
 */

/** Statuses the pipeline still moves on its own, so the list is worth refreshing while any are present. */
const IN_PROGRESS_STATUSES = ['Pending', 'Processing', 'Extracted'];

/** How many bills are still being read: the summary's count for the whole company, else this page's. */
function countInProgress(countsByStatus, items) {
  return countsByStatus ? IN_PROGRESS_STATUSES.reduce((total, status) => {
    var _countsByStatus$statu;
    return total + ((_countsByStatus$statu = countsByStatus[status]) !== null && _countsByStatus$statu !== void 0 ? _countsByStatus$statu : 0);
  }, 0) : items.filter(item => IN_PROGRESS_STATUSES.includes(item.extractStatus)).length;
}

/**
 * The one AP push state something else is still working on. A push is queued here and finished by
 * AppFolio through Kafka, so the row settles on a later read rather than on the call that started it.
 * `failed` and `needsReconciliation` are settled: they wait on a person, not on the pipeline.
 */
const UNSETTLED_AP_PUSH_STATUSES = ['pending'];

/**
 * localStorage genuinely holds the strings 'null' and 'undefined' when no company is selected, and a
 * bare falsy check lets both through — which builds a request to /companies/null/bills. The allowed
 * senders store already guards all three; this is the same guard.
 */
const getCurrentCompanyId = () => {
  const stored = localStorage.getItem(USER_SETTINGS_KEYS.company);
  return !stored || stored === 'null' || stored === 'undefined' ? '' : stored;
};

/**
 * One page of the list.
 *
 * It used to walk up to twenty pages and hold every row, because the table filtered and sorted in
 * the browser over things the service could not filter on at all. The service can now, so this asks
 * for exactly what is on screen.
 */
const getCompanyBillsAsync = createAsync(params => {
  return async function getCompanyBills(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;

    // Dropped before the read, not after it, and for the same reason the summary drops its own:
    // rows left standing here belong to the PREVIOUS company, and the page cannot tell -- the
    // batch scope is null on both sides, so it reads them as current and renders them.
    this.forgetPreviousCompany(companyId);

    // Recorded before the await, so a refresh knows what to ask for again even while this is still
    // in flight -- and so a poll cannot quietly re-read the list under different filters.
    this.setRequestedQuery(params);
    const resp = await api.BillAutomationBills.getCompanyBills(_objectSpread({
      companyId
    }, params), options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful && resp.response) this.setCompanyBills(resp.response, params);
    return resp;
  };
});

/**
 * When each bill on the page was ready to collect (LB-6032), for the Bill ready column. Silent: the column decorates
 * the list, and a read that failed shows a dash rather than a toast over the table.
 *
 * Batched inside the one action rather than run once per batch: `run` cancels the call still in flight on the same
 * action, so a page asked about in two runs would only ever hear back about its second half.
 */
const getBillReadyDatesAsync = createSilentAsync(body => {
  return async function getBillReadyDates(options) {
    const batches = [];
    for (let start = 0; start < body.bills.length; start += BILL_READY_BATCH) {
      batches.push(body.bills.slice(start, start + BILL_READY_BATCH));
    }
    const answers = await Promise.all(batches.map(batch => api.BillReady.postForBills(batch, options)));

    // A batch that says it failed is a failed read, not an empty one: half a page, or dashes over dates already shown,
    // would be the wrong answer. Thrown, so the silent action answers nothing and the page keeps what it had.
    if (answers.some(answer => !(answer !== null && answer !== void 0 && answer.isSuccessful) || !Array.isArray(answer.response))) {
      throw new Error('Bill ready dates could not be read.');
    }
    return {
      isSuccessful: true,
      response: answers.flatMap(answer => {
        var _answer$response;
        return (_answer$response = answer.response) !== null && _answer$response !== void 0 ? _answer$response : [];
      })
    };
  };
});

/**
 * What the filter dropdowns can offer. Silent: they decorate a page whose job is the list, and a
 * dropdown that could not load should be empty and say so rather than raise a toast over the table.
 */
const getBillFilterOptionsAsync = createSilentAsync(() => {
  return async function getBillFilterOptions(options) {
    const companyId = getCurrentCompanyId();

    // Dropped before the read, as the summary is: the store outlives a company switch, and if the new company's read
    // failed its Service provider filter would offer the previous company's providers, with no Retry (LB-6087).
    if (this.filterOptionsCompanyId !== companyId) this.clearFilterOptions();
    if (!companyId) return undefined;
    const resp = await api.BillAutomationBills.getCompanyBillFilterOptions({
      companyId
    }, options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful && resp.response) this.setFilterOptions(resp.response, companyId);
    return resp;
  };
});
const getCompanyBillsSummaryAsync = createSilentAsync(() => {
  return async function getCompanyBillsSummary(options) {
    var _resp;
    const companyId = getCurrentCompanyId();

    // Dropped before the read, not after it. The store outlives a company switch, so figures left
    // standing here are the PREVIOUS company's — and if the new read then fails they stay on screen
    // indefinitely, under the new company's name.
    //
    // Checked before the no-company bail, too: no company is a scope like any other, and bailing
    // first leaves one company's figures on screen under an account that has none selected.
    if (this.summaryCompanyId !== companyId) this.clearSummary();
    if (!companyId) return undefined;
    let resp;
    try {
      resp = await api.BillAutomationBills.getCompanyBillsSummary({
        companyId
      }, options);
    } catch (error) {
      // A cancelled read is this request being replaced by a newer one. The figures it was
      // refreshing are still the right ones to show until the replacement lands; clearing here
      // blanks good tiles every time a refresh interrupts the poll.
      if (isHttpClientCancel(error)) throw error;
      this.clearSummary();
      throw error;
    }
    if ((_resp = resp) !== null && _resp !== void 0 && _resp.isSuccessful && resp.response) this.setSummary(resp.response, companyId);
    // An answer we cannot read is not an answer. Holding the last one would show stale figures as
    // current, and the tiles have a state for "not available" precisely so they need not.
    else this.clearSummary();
    return resp;
  };
});

/**
 * The company's newest bills (LB-6034), for the upload dialog. It shows every file's status, and an upload of up to 100
 * files reaches past the page the table has loaded. Silent, and apart from the table's own read: the rows only add to
 * what the page knows about each bill, so the view on screen doesn't move.
 */
const getRecentBillsAsync = createSilentAsync(() => {
  return async function getRecentBills(options) {
    var _resp$response$items;
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;

    // As many as the service sends in one page, which is as many as one upload can hold.
    const resp = await api.BillAutomationBills.getCompanyBills({
      companyId,
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      sortBy: 'acquired',
      sortDescending: true
    }, options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful && resp.response) this.rememberBills((_resp$response$items = resp.response.items) !== null && _resp$response$items !== void 0 ? _resp$response$items : [], companyId);
    return resp;
  };
});

/**
 * The same answer, read only for how many allocations were made from the bill (LB-5952), for a delete's confirmation.
 * Silent, and its own instance: the confirmation words itself without it if it fails, and the allocation card's reads
 * must not cancel it.
 */
const countBillAllocationsAsync = createSilentAsync(body => {
  return async function countBillAllocations(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;
    return api.BillAutomationBills.getBillAllocations({
      companyId,
      billId: body.billId
    }, options);
  };
});

/**
 * Where every service on the bill stands, read without changing anything: the allocation card's first read, and
 * its poll while a table is being worked out. Only a read, so opening a bill can never approve it (LB-6052).
 */
const getBillAllocationsAsync = createAsync(body => {
  return async function getBillAllocations(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;
    return api.BillAutomationBills.getBillAllocations({
      companyId,
      billId: body.billId
    }, options);
  };
});

/**
 * A short-lived presigned URL for the bill's stored document (LB-5724 PR A / LB-5755 C1). Silent:
 * a failed or absent preview degrades to a "no document" state in the details view, never a toast.
 * The caller opens the url in an iframe / new tab and never caches it beyond expiresInSeconds.
 */
const getBillFileUrlAsync = createSilentAsync(body => {
  return async function getBillFileUrl(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;
    return api.BillAutomationBills.getBillFileUrl({
      companyId,
      billId: body.billId
    }, options);
  };
});

/**
 * Creates the allocation bill for each service named. The table is worked out after the call, and
 * stops at "Awaiting approval": approving it is `approveBillAsync`, a click of its own (LB-6052).
 */
const allocateBillAsync = createAsync(body => {
  return async function allocateBill(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;
    return api.BillAutomationBills.allocateBill({
      companyId,
      billId: body.billId,
      confirmedOldServicePeriod: body.confirmedOldServicePeriod,
      services: body.services
    }, options);
  };
});

/**
 * Approves the tables named, which charges the residents on them (LB-6052). Its own instance, not
 * allocate's: one must never cancel the other while it is on its way.
 */
const approveBillAsync = createAsync(body => {
  return async function approveBill(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;
    return api.BillAutomationBills.approveBill({
      companyId,
      billId: body.billId,
      tables: body.tables
    }, options);
  };
});

/**
 * The blocks the attach dialog offers (LB-5910). Silent: when it fails the dialog falls back to one
 * property for the whole bill, which is how every bill was attached before, so there is nothing to
 * interrupt anybody with.
 */
const getBillAttachOptionsAsync = createSilentAsync(body => {
  return async function getBillAttachOptions(options) {
    return api.BillAutomationBills.getBillAttachOptions(body, options);
  };
});

/**
 * Deletes the bill, and drops it from the list when the service says it is gone. A bill held by its
 * allocations comes back refused with the reason, and stays where it is.
 */
/**
 * Approve-on-attach (LB-5766). Bill-scoped route, so no company id — the service settles ownership
 * against the bill's own company. The refreshed list picks up the new status (Processing/Extracted
 * are in-progress, so the list poller re-arms on its own).
 */
const attachBillAsync = createAsync(body => {
  return async function attachBill(options) {
    return api.BillAutomationBills.attachBill(body, options);
  };
});
const deleteBillAsync = createAsync(body => {
  return async function deleteBill(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;
    const resp = await api.BillAutomationBills.deleteBill({
      companyId,
      billId: body.billId
    }, options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful) this.removeBill(body.billId);
    return resp;
  };
});

/**
 * What a retry may offer for this bill (LB-5787). Silent: the retry dialog says itself when it
 * couldn't check, so a toast on top would say it twice.
 */
const getBillRetryOptionsAsync = createSilentAsync(body => {
  return async function getBillRetryOptions(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;
    return api.BillAutomationBills.getBillRetryOptions({
      companyId,
      billId: body.billId
    }, options);
  };
});

/**
 * Retry a bill (LB-5787). With `new`, the file goes up exactly as an upload does — straight to
 * storage, never through the API — and only its key is sent with the retry. On success the retried
 * bill is archived, so it leaves the list; its replacement arrives on the next read of the page.
 */
const retryBillAsync = createAsync(body => {
  return async function retryBill(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;
    const resp = body.mode === 'new' ? await (async () => {
      const stored = await storeBillFile({
        file: body.file,
        contentType: body.contentType
      }, options);
      return api.BillAutomationBills.retryBill(_objectSpread({
        companyId,
        billId: body.billId,
        mode: 'new',
        fileKey: stored.fileKey,
        fileName: body.file.name,
        contentType: body.contentType
      }, stored.sha256 ? {
        sha256: stored.sha256
      } : {}), options);
    })() : await api.BillAutomationBills.retryBill({
      companyId,
      billId: body.billId,
      mode: 'existing'
    }, options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful) this.removeBill(body.billId);
    return resp;
  };
});

/** Company-wide bill list behind the Bills page table. */
const CompanyBillsStore = types.model('CompanyBillsStore', {
  items: types.optional(types.frozen(), []),
  total: types.optional(types.number, 0),
  page: types.optional(types.number, 1),
  pageSize: types.optional(types.number, DEFAULT_PAGE_SIZE),
  /**
   * Which batch scope the loaded `items` belong to (LB-5828): the `batchId` of the request that
   * produced them, or null for the whole-company list. Lets the page tell "these rows are for the
   * scope I'm showing" from "these are last scope's rows, a refetch is on the way" — so it never
   * presents one scope's rows as another's while a scoped fetch is in flight or after one fails.
   */
  /**
   * The query the loaded `items` answer, and the one most recently asked for. The page compares
   * their scope so it never presents one acquisition event's rows as another's while a scoped read
   * is in flight, and `refetchCurrent` re-asks the second without the caller having to remember it.
   */
  loadedQuery: types.maybeNull(types.frozen()),
  requestedQuery: types.maybeNull(types.frozen()),
  /**
   * Every row seen this session, by id.
   *
   * The table holds one page now, but several things still ask about a bill by id — the details
   * drawer, the attach modal, the row a deep link names — and they used to find it because every
   * row was loaded. Kept per company: another company's rows resolving here would be worse than
   * not finding one at all.
   */
  knownById: types.optional(types.frozen(), {}),
  knownCompanyId: types.maybeNull(types.string),
  /**
   * Whether the view on screen is one the server must not be asked for.
   *
   * Distinct from `requestedQuery` being null, which only means nobody has asked yet. The two
   * had been the same thing, and a refresh reads the second as "ask for a plain first page" --
   * so the poller, an upload and the Refresh button each repopulated the deliberately empty view
   * with the whole company, under a filter still saying it matched nothing.
   */
  nothingToAsk: types.optional(types.boolean, false),
  /** What the filter dropdowns offer, or null until the first read lands. */
  filterOptions: types.maybeNull(types.frozen()),
  /** Which company the options above are for, so another company's are never offered as theirs. */
  filterOptionsCompanyId: types.maybeNull(types.string),
  /** The tiles' figures, or null until the first read lands. */
  summary: types.maybeNull(types.frozen()),
  /** Which company the figures above describe, so another company's can never be shown as theirs. */
  summaryCompanyId: types.maybeNull(types.string),
  allocateBillAsync,
  approveBillAsync,
  attachBillAsync,
  getBillAttachOptionsAsync,
  deleteBillAsync,
  getBillRetryOptionsAsync,
  retryBillAsync,
  getBillAllocationsAsync,
  countBillAllocationsAsync,
  getBillFileUrlAsync,
  getBillFilterOptionsAsync,
  getBillReadyDatesAsync,
  getCompanyBillsAsync,
  getCompanyBillsSummaryAsync,
  getRecentBillsAsync
}).actions(store => ({
  setCompanyBills(page, params) {
    var _page$items, _page$total, _page$page, _page$pageSize;
    const items = (_page$items = page.items) !== null && _page$items !== void 0 ? _page$items : [];
    store.items = items;
    store.total = (_page$total = page.total) !== null && _page$total !== void 0 ? _page$total : 0;
    store.page = (_page$page = page.page) !== null && _page$page !== void 0 ? _page$page : 1;
    store.pageSize = (_page$pageSize = page.pageSize) !== null && _page$pageSize !== void 0 ? _page$pageSize : DEFAULT_PAGE_SIZE;
    store.loadedQuery = params;

    // Restated rather than merged over: a row read again is the newer truth about it.
    store.knownById = items.reduce((known, item) => _objectSpread(_objectSpread({}, known), {}, {
      [item.billId]: item
    }), store.knownById);
    store.knownCompanyId = getCurrentCompanyId();
  },
  /**
   * Adds rows to what the page knows about each bill, and nothing else: the table's rows, total and query stay as they
   * are. A read that answers after a company switch is dropped, as it describes the company before.
   */
  rememberBills(items, companyId) {
    if (companyId !== getCurrentCompanyId()) return;
    if (store.knownCompanyId && store.knownCompanyId !== companyId) return;
    store.knownById = items.reduce((known, item) => _objectSpread(_objectSpread({}, known), {}, {
      [item.billId]: item
    }), store.knownById);
    store.knownCompanyId = companyId;
  },
  setRequestedQuery(params) {
    store.requestedQuery = params;
    store.nothingToAsk = false;
  },
  /**
   * Reads the view on screen again — after a delete, an attach, a push, or a poll tick.
   *
   * One action rather than each caller rebuilding the query, because a caller that rebuilt it
   * slightly differently would quietly move the table out from under somebody: the poll used to
   * re-read with `{ batchId }` alone, which after this change would drop every filter they set.
   *
   * `quiet` is for the poll, whose failures are said once, when it gives up, rather than as a toast
   * every few seconds (LB-5982). Everything else re-reads because someone just did something, and is
   * told when that fails.
   */
  refetchCurrent(options) {
    var _store$requestedQuery;
    // Nothing to re-read when there was nothing to ask for. Without this the fallback below
    // turns "this view is impossible" into "read the whole company", which is how the poller
    // put 25 unfiltered rows under a filter that matched none of them.
    if (store.nothingToAsk) return undefined;

    // Falls back to a plain first page when nothing has been asked for yet. A caller refreshing a
    // list nobody has read is unusual but not wrong -- an upload finishing before the table's own
    // first read has landed, say -- and doing nothing there would leave it empty with no way back.
    //
    // Returned rather than fired and forgotten: the poller waits for it, because `run` cancels
    // whatever is still in flight on the same action, and an unawaited read would be aborted by
    // the next tick rather than finishing.
    return store.getCompanyBillsAsync.run((_store$requestedQuery = store.requestedQuery) !== null && _store$requestedQuery !== void 0 ? _store$requestedQuery : {}, [], {
      silent: !!(options !== null && options !== void 0 && options.quiet)
    });
  },
  /** Forgets the rows of a company that is no longer the one being looked at. */
  clearKnown() {
    store.knownById = {};
    store.knownCompanyId = null;
  },
  /**
   * Empties the list because nothing is being asked for.
   *
   * There is one view that sends no request at all: a tab and a status filter that cannot both
   * be satisfied. Without this the page simply skipped the fetch and left the previous view on
   * screen -- rows whose status pill contradicted the filter beside them, still selectable and
   * still bulk-deletable, and kept fresh by the poller because `requestedQuery` was still the
   * last satisfiable one.
   *
   * `requestedQuery` is cleared with them, so a refresh cannot re-read the query this view
   * replaced.
   */
  clearList() {
    store.items = [];
    store.total = 0;
    store.page = 1;
    store.loadedQuery = null;
    store.requestedQuery = null;
    store.nothingToAsk = true;
  },
  /**
   * Drops one company’s rows before another’s are read.
   *
   * The store outlives the company selector, and nothing here was scoped to a company: `items`
   * stayed put across the switch, and since the batch scope is null on both sides the page read
   * them as current and rendered them -- one company’s bills, under another company’s name,
   * until the first read for the new one landed. The summary has guarded itself this way since
   * LB-5839; the list simply never did.
   */
  /**
   * Drops the previous company’s rows when the one being read is a different one.
   *
   * The comparison lives in the action rather than in the async function that calls it, because
   * `this` there exposes the actions and not the model’s own fields -- read from outside, the
   * company it was holding was simply undefined and the guard never fired.
   */
  forgetPreviousCompany(companyId) {
    if (store.knownCompanyId && store.knownCompanyId !== companyId) this.forgetCompany();
  },
  forgetCompany() {
    store.items = [];
    store.total = 0;
    store.page = 1;
    store.loadedQuery = null;
    store.knownById = {};
    store.knownCompanyId = null;
  },
  setFilterOptions(options, companyId) {
    store.filterOptions = options;
    store.filterOptionsCompanyId = companyId;
  },
  clearFilterOptions() {
    store.filterOptions = null;
    store.filterOptionsCompanyId = null;
  },
  setSummary(summary, companyId) {
    store.summary = summary;
    store.summaryCompanyId = companyId;
  },
  clearSummary() {
    store.summary = null;
    store.summaryCompanyId = null;
  },
  /**
   * Takes a deleted bill out of the list without waiting for the next read of it.
   *
   * Deliberately does its work whether or not the bill is on the page in hand. It used to bail
   * out when it was not, which was harmless while `items` held the whole book -- not being in it
   * meant not existing. Over one page it means the commonest case there is: a bulk delete of a
   * selection made across pages, where every bill but the ones on screen would have left `total`
   * counting them and `knownById` still answering for them.
   */
  removeBill(billId) {
    const remaining = store.items.filter(item => item.billId !== billId);
    const wasOnThisPage = remaining.length !== store.items.length;
    if (wasOnThisPage) store.items = remaining;
    const _store$knownById = store.knownById,
      deleted = _store$knownById[billId],
      rest = _objectWithoutProperties(_store$knownById, [billId].map(_toPropertyKey));
    if (deleted) store.knownById = rest;

    // One fewer bill in the book either way. The page it was on does not come into it.
    store.total = Math.max(store.total - 1, 0);
  }
})).views(store => ({
  /**
   * Whether anything is still moving, which is what keeps the list refreshing.
   *
   * Read from the summary, which counts the whole company, rather than from the page on screen.
   * Asked of one page it would answer "is anything in progress among the twenty-five rows you
   * happen to be looking at" -- so a bill extracting on another page, or hidden by a filter, would
   * never arm the refresh and would never visibly settle. The page is the fallback only until the
   * first summary lands.
   *
   * And the page can only add to it. A row on screen still in progress while the summary says
   * nothing is was read a moment before its bill settled -- the list and the summary are two reads,
   * and a bill can finish between them -- so it is read again rather than left showing as extracting
   * until somebody presses Refresh (LB-5979).
   */
  get hasBillsInProgress() {
    var _store$summary;
    return countInProgress((_store$summary = store.summary) === null || _store$summary === void 0 ? void 0 : _store$summary.countsByStatus, store.items) > 0 || store.items.some(item => IN_PROGRESS_STATUSES.includes(item.extractStatus));
  },
  /** More of them than before is new work, which wakes a list poll that gave up (LB-5944). */
  get billsInProgressCount() {
    var _store$summary2;
    return countInProgress((_store$summary2 = store.summary) === null || _store$summary2 === void 0 ? void 0 : _store$summary2.countsByStatus, store.items);
  },
  /**
   * The bills whose push is still on its way, which is what keeps the list refreshing. A new one is what
   * wakes a list poll that gave up on the others (LB-5944).
   */
  get unsettledApPushBillIds() {
    return store.items.filter(item => {
      var _item$apPush;
      return UNSETTLED_AP_PUSH_STATUSES.includes((_item$apPush = item.apPush) === null || _item$apPush === void 0 ? void 0 : _item$apPush.status);
    }).map(item => item.billId);
  },
  /**
   * A bill seen this session, by id. Not "a bill on the page currently loaded", which is what it
   * used to mean and would now be far narrower than every caller expects.
   */
  getBill(billId) {
    return store.knownById[billId];
  },
  /** Whether a bill seen this session has come out of extraction. Not seen yet counts as not. */
  isBillSettled(billId) {
    const row = store.knownById[billId];
    return !!row && !IN_PROGRESS_STATUSES.includes(row.extractStatus);
  },
  /** The acquisition event the loaded rows belong to, or null for the whole-company list. */
  get loadedBatchId() {
    var _ref, _store$loadedQuery;
    return (_ref = (_store$loadedQuery = store.loadedQuery) === null || _store$loadedQuery === void 0 ? void 0 : _store$loadedQuery.batchId) !== null && _ref !== void 0 ? _ref : null;
  }
}));
export const companyBills = types.optional(CompanyBillsStore, {});